import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useUsersLogoutCreate } from "@/services/auth/generated/users/users";
import { useAuthStore } from "@/store/authStore";

interface UserMenuProps {
  /** Custom className for the trigger button */
  className?: string;
  /** Alignment of the dropdown menu */
  align?: "start" | "center" | "end";
  /** Side offset for the dropdown */
  sideOffset?: number;
}

/**
 * Reusable user menu component with avatar, profile, and logout functionality
 *
 * @example
 * ```tsx
 * // Default usage
 * <UserMenu />
 *
 * // With custom styling
 * <UserMenu className="custom-avatar" align="end" sideOffset={8} />
 * ```
 */
export const UserMenu = ({ className, align = "end", sideOffset = 8 }: UserMenuProps) => {
  const { t } = useTranslation("layout");
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const clearAuth = useAuthStore((state) => state.clearAuth);

  const logoutMutation = useUsersLogoutCreate({
    mutation: {
      onSuccess: () => {
        clearAuth();
        navigate("/b2b/login");
      },
      onError: (error) => {
        console.error("Logout error:", error); // i18n-ignore
        clearAuth();
        navigate("/b2b/login");
      },
    },
  });

  // Get user initials for avatar
  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return "AB";
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.email) {
      return user.email;
    }
    return t("userMenu.guest");
  };

  const handleProfileClick = () => {
    navigate("/b2b/profile");
  };

  const handleLogoutClick = () => {
    logoutMutation.mutate();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "dashboard__avatar-button",
            "relative inline-flex items-center justify-center",
            "focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary",
            className,
          )}
          aria-label={t("userMenu.label")}
        >
          <Avatar className="h-10 w-10 rounded-[16px]">
            <AvatarFallback className="dashboard__avatar-initials bg-linear-to-br from-primary to-blue-600 text-white text-[0.8rem] font-semibold rounded-[16px] shadow-md">
              {getUserInitials()}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align={align} sideOffset={sideOffset} className="w-[280px] p-0">
        {/* User Info Header */}
        <div className="flex items-center gap-3 p-4 pb-3">
          <Avatar className="h-12 w-12 rounded-xl">
            <AvatarFallback className="bg-linear-to-br from-primary to-blue-600 text-white text-base font-semibold rounded-xl">
              {getUserInitials()}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col flex-1 min-w-0">
            <DropdownMenuLabel className="p-0 mb-0.5 text-sm font-semibold text-foreground truncate">
              {getUserDisplayName()}
            </DropdownMenuLabel>
            {user?.email && <p className="text-xs text-muted-foreground truncate">{user.email}</p>}
          </div>
        </div>

        <DropdownMenuSeparator className="my-0" />

        {/* Menu Items */}
        <div className="p-1">
          <DropdownMenuItem onClick={handleProfileClick} className="cursor-pointer px-3 py-2.5 rounded-md">
            <i className="fa-regular fa-user w-5 text-muted-foreground" />
            <span className="ml-2">{t("userMenu.myProfile")}</span>
          </DropdownMenuItem>

          <DropdownMenuSeparator className="my-1" />

          <DropdownMenuItem
            onClick={handleLogoutClick}
            disabled={logoutMutation.isPending}
            className="cursor-pointer px-3 py-2.5 rounded-md text-destructive focus:text-destructive focus:bg-destructive/10"
          >
            <i className="fa-solid fa-arrow-right-from-bracket w-5" />
            <span className="ml-2">{logoutMutation.isPending ? t("userMenu.loggingOut") : t("userMenu.logout")}</span>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
